import React, { FC, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useStore } from '../../store/store-provider';
import { QuestionReviewModeContainer, TabNav, Tabs } from '../index';
import QnadaButtonGroup from './qanda-button-group/qnada-button-group';
import './qanda.scss';
import { QandaTabs, QuestionState } from '../../constans/question-enums';
import QuestionsContainer from './questions/questions-container';
import QuestionEntity from '../../store/question-store/question-entetiy';
import ReviewModePopups from './questions/review-mode-popups/review-mode-popups';
import QandASubscriptionHOC from './qanda-subscription-hoc';
import { getPodiumInfoStore } from '../../store/podium-info-store/podium-info-store-instance';
import { DESKTOP_BREAKPOINT } from '../../constans/responsive-break-points';
import { QandaPermissionHOC } from './qanda-permissions-hoc';
import { getMixpanelService } from '../../utils/services/mixpanel-service-instance';
import { MIXPANEL_EVENT_IDENTIFIERS, MIXPANEL_EVENT_TYPES } from '../../constans/mixpanel-contants';
import { Permissions } from '../../store/permissions-store/permissions-store';

interface IQandaCustomTabs {
    selected: string;
    isReviewMode: boolean;
    isMobile: boolean;
    presentationId: string | null;
}

export type TTabs = {
    title: QandaTabs;
    questions: QuestionEntity[];
    subTitle?: string;
    numberOfNewQuestions?: number;
}

const UseQandaCustomTabs = ({ selected, isReviewMode, isMobile }: IQandaCustomTabs) => {
  const isReviewModeTab: boolean = selected === QandaTabs.UNANSWERED && isReviewMode;
  const { questionStore } = useStore();
  const { getQuestionList } = questionStore;

  useEffect(() => {
    getQuestionList();
  }, []);

  return (props: TTabs) => (
    isReviewModeTab
      ? <QuestionReviewModeContainer {...props} isMobile={isMobile}/>
      : <QuestionsContainer {...props}/>
  );
};

const QandA: FC = observer((): JSX.Element => {
  const { podiumInfoStore, questionStore } = useStore();
  const {
    getList,
    getReviewList,
    downloadQuestionCSV,
    questionList,
    getManageQuestionNewLabel,
    toggleOpenMenu,
    isMoreMenuOpen
  } = questionStore;

  const {
    toggleReviewMode,
    confirmReviewModeChange,
    applyReviewModeChanges,
    isReviewMode,
    isConfirmationRequired,
    updateSelectedTab,
    selectedTab,
    isShowReviewModePopUp,
    tmpReviewModeState,
    isResolutionBreakPointActive
  } = podiumInfoStore;

  const presentationId = getPodiumInfoStore().getPresentationId();
  const isMobile = isResolutionBreakPointActive(DESKTOP_BREAKPOINT);
  const tabs: TTabs[] = [
    {
      title: QandaTabs.UNANSWERED,
      questions: getReviewList(),
      numberOfNewQuestions: getManageQuestionNewLabel().onSubscribeNewQuestionStatus(QuestionState.REVIEW),
    },
    {
      title: QandaTabs.ANSWERED,
      questions: getList(QuestionState.ANSWERED),
    },
    {
      title: QandaTabs.REMOVED,
      questions: getList(QuestionState.REMOVED),
    }
  ];

  const selectTabHandler = (tabName: QandaTabs): void => {
    updateSelectedTab(tabName);

    let tabNameType = null;

    if (tabName === QandaTabs.ANSWERED) {
      tabNameType = MIXPANEL_EVENT_TYPES.ANSWERED_TAB_CLICKED;
    }
    if (tabName === QandaTabs.UNANSWERED) {
      tabNameType = MIXPANEL_EVENT_TYPES.UNANSWERED_TAB_CLICKED;
    }
    if (tabName === QandaTabs.REMOVED) {
      tabNameType = MIXPANEL_EVENT_TYPES.REMOVED_TAB_CLICKED;
    }

    getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.QATab, tabNameType);
  };

  return (
    <QandaPermissionHOC shouldRedirect={true} variant={Permissions.QandA}>
      <div className="kv-presenter-qanda">
        <QandASubscriptionHOC>
          <div className="kv-presenter-qanda__nav-bar">
            <TabNav tabs={tabs} selected={selectedTab} selectTabHandler={selectTabHandler}/>
            <QnadaButtonGroup
              isReviewMode={isReviewMode}
              downloadCSV={() => downloadQuestionCSV()}
              questionsExist={!!questionList.length}
              toggleReviewMode={toggleReviewMode}
              toggleOpenMenu={toggleOpenMenu}
              isMoreMenuOpen={isMoreMenuOpen}
            />
            <ReviewModePopups
              tmpReviewModeState={tmpReviewModeState}
              toggleReviewMode={toggleReviewMode}
              applyReviewModeChanges={applyReviewModeChanges}
              isConfirmationRequired={isConfirmationRequired}
              confirmReviewModeChange={confirmReviewModeChange}
              presentationId={presentationId}
              isShowReviewModePopUp={isShowReviewModePopUp}
            />
          </div>
          <section className="kv-presenter-qanda__workspace">
            <Tabs
              tabs={tabs}
              selected={selectedTab}
              component={UseQandaCustomTabs({ selected: selectedTab, isReviewMode, isMobile, presentationId })}
            />
          </section>
        </QandASubscriptionHOC>
      </div>
    </QandaPermissionHOC>
  );
});

export default QandA;
